import { useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { usePortalRole } from "@/hooks/usePortalRole";
import PortalLayout from "@/components/portal/PortalLayout";
import LoadingScreen from "@/components/LoadingScreen";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import IntroducerAgreementSign from "./IntroducerAgreementSign";

const AGREEMENT_VERSION = "v1";

interface IntroducerProfileRow {
  user_id: string;
  status: string | null;
  compliance_status: string | null;
  code_of_conduct_accepted_at: string | null;
  dpa_acknowledged_at: string | null;
}

function isCompliant(row: IntroducerProfileRow | null): boolean {
  if (!row) return false;
  return (
    row.status === "active" &&
    row.compliance_status === "accepted" &&
    !!row.code_of_conduct_accepted_at &&
    !!row.dpa_acknowledged_at
  );
}

interface Props {
  children: ReactNode;
}

/**
 * Gate that blocks introducer portal pages until the user has accepted the
 * Code of Conduct and Data Processing acknowledgement. Admins (users that
 * also hold the admin role) bypass the block but see a non-blocking banner
 * when viewing an unaccepted introducer's hub.
 */
export default function IntroducerComplianceGate({ children }: Props) {
  const { userId, roles, loading: roleLoading } = usePortalRole();
  const isAdmin = roles.includes("admin");

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<IntroducerProfileRow | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [agreementRequires, setAgreementRequires] = useState<boolean | null>(null);
  const [agreementReason, setAgreementReason] = useState<string | null>(null);

  // Form state
  const [confirmCoc, setConfirmCoc] = useState(false);
  const [confirmDpa, setConfirmDpa] = useState(false);
  const [confirmConduct, setConfirmConduct] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadProfile = async () => {
    if (!userId) return;
    setLoading(true);
    setFetchError(null);
    const [profRes, resignRes] = await Promise.all([
      supabase
        .from("introducer_profiles")
        .select("user_id, status, compliance_status, code_of_conduct_accepted_at, dpa_acknowledged_at")
        .eq("user_id", userId)
        .maybeSingle(),
      (supabase as any).rpc("requires_resign", {
        _user_id: userId,
        _template_type: "introducer_agreement",
      }),
    ]);
    if (profRes.error) {
      setFetchError(profRes.error.message);
      setProfile(null);
    } else {
      setProfile(profRes.data as IntroducerProfileRow | null);
    }
    if (resignRes?.error) {
      // Fail closed: treat as requires-sign so tools stay locked safely.
      setAgreementRequires(true);
      setAgreementReason("rpc_error");
    } else if (resignRes?.data) {
      setAgreementRequires(!!resignRes.data.requires);
      setAgreementReason(resignRes.data.reason ?? null);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!roleLoading && userId) {
      void loadProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleLoading, userId]);

  const handleAccept = async () => {
    if (!confirmCoc || !confirmDpa || !confirmConduct) {
      toast.error("Please confirm all three acknowledgements before continuing.");
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.rpc("accept_introducer_compliance", {
        p_agreement_version: AGREEMENT_VERSION,
      });
      if (error) {
        toast.error(error.message || "We could not record your acceptance. Please try again.");
        return;
      }
      toast.success("Thank you. Your introducer hub is now unlocked.");
      await loadProfile();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Unexpected error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (roleLoading || loading) return <LoadingScreen />;

  const compliant = isCompliant(profile);
  const agreementOk = agreementRequires === false;
  const fullyUnlocked = compliant && agreementOk;

  // Admin bypass — show non-blocking banner if viewing an unaccepted hub.
  if (isAdmin && !fullyUnlocked) {
    return (
      <>
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-xs text-amber-900 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>
            Admin preview: this introducer has not yet completed{" "}
            {!compliant ? "the Code of Conduct / DPA" : "the Field Introducer Agreement signing"}
            {agreementRequires && agreementReason ? ` (${agreementReason})` : ""}. A real introducer would see the gate.
          </span>
        </div>
        {children}
      </>
    );
  }

  if (fullyUnlocked) return <>{children}</>;

  // Compliance done, but agreement signing required (or re-sign).
  if (compliant && userId && agreementRequires) {
    return (
      <PortalLayout title="Field Introducer Agreement">
        <div className="max-w-3xl mx-auto">
          <IntroducerAgreementSign userId={userId} onSigned={loadProfile} />
        </div>
      </PortalLayout>
    );
  }


  // No introducer profile row — show a clear message.
  if (!profile) {
    return (
      <PortalLayout title="Introducer Hub">
        <Card>
          <CardHeader>
            <CardTitle>Introducer profile not found</CardTitle>
            <CardDescription>
              Your account does not have an active introducer profile.
              {fetchError ? ` (${fetchError})` : ""} Please contact the EvoHome Improvements team.
            </CardDescription>
          </CardHeader>
        </Card>
      </PortalLayout>
    );
  }

  const allChecked = confirmCoc && confirmDpa && confirmConduct;

  return (
    <PortalLayout title="Welcome — Confirm Your Introducer Agreement">
      <div className="max-w-3xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div className="space-y-1">
                <CardTitle>Before you start as an EvoHome Improvements Introducer</CardTitle>
                <CardDescription>
                  Please read the points below carefully. You will not be able to access the introducer tools until you confirm your acknowledgement.
                </CardDescription>
                <Badge variant="outline" className="mt-2 text-xs">
                  Status: {profile.status ?? "unknown"} · Compliance: {profile.compliance_status ?? "not_started"}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <section className="space-y-3">
              <h3 className="font-semibold text-foreground">What an introducer is, and is not</h3>
              <ul className="list-disc pl-5 space-y-2 text-sm text-muted-foreground">
                <li>Introducers do not provide quotes to homeowners.</li>
                <li>Introducers do not price work.</li>
                <li>Introducers do not promise that EvoHome Improvements will install or carry out works.</li>
                <li>
                  EvoHome Improvements acts as a coordinator, platform, consultancy and commercial facilitator. We work with independent installers and specialists who carry out the actual works.
                </li>
              </ul>
            </section>

            <section className="space-y-3">
              <h3 className="font-semibold text-foreground">How you must treat homeowners</h3>
              <ul className="list-disc pl-5 space-y-2 text-sm text-muted-foreground">
                <li>You must only submit homeowners who have agreed to be contacted by EvoHome Improvements.</li>
                <li>You must not pressure people. A polite "no" ends the conversation.</li>
                <li>You must not mislead people about EvoHome Improvements, our services, or the works that may follow.</li>
                <li>
                  You must not make claims about savings, grants, finance, guarantees, or installer availability unless those claims have been approved in writing by EvoHome Improvements.
                </li>
              </ul>
            </section>

            <section className="space-y-3">
              <h3 className="font-semibold text-foreground">Personal data and disclosure</h3>
              <ul className="list-disc pl-5 space-y-2 text-sm text-muted-foreground">
                <li>
                  You must handle homeowners' personal details carefully and use them only for approved EvoHome Improvements purposes.
                </li>
                <li>
                  You must disclose that you may receive a reward or payment for the introduction where this is legally required, or where the homeowner asks.
                </li>
              </ul>
            </section>

            <section className="space-y-3">
              <h3 className="font-semibold text-foreground">How payment works</h3>
              <ul className="list-disc pl-5 space-y-2 text-sm text-muted-foreground">
                <li>
                  Payment is not made simply for submitting a name or a raw lead. Payment depends on EvoHome Improvements' approved commissionable event policy, which is shared with you separately.
                </li>
              </ul>
            </section>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Confirm and continue</CardTitle>
            <CardDescription>Please tick all three boxes to unlock your introducer hub.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={confirmCoc} onCheckedChange={(v) => setConfirmCoc(v === true)} className="mt-0.5" />
              <Label className="font-normal text-sm leading-relaxed cursor-pointer">
                I confirm I have read and agree to the EvoHome Improvements Introducer Code of Conduct.
              </Label>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={confirmDpa} onCheckedChange={(v) => setConfirmDpa(v === true)} className="mt-0.5" />
              <Label className="font-normal text-sm leading-relaxed cursor-pointer">
                I confirm I understand my data protection responsibilities when handling homeowners' personal information.
              </Label>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={confirmConduct} onCheckedChange={(v) => setConfirmConduct(v === true)} className="mt-0.5" />
              <Label className="font-normal text-sm leading-relaxed cursor-pointer">
                I confirm I will not quote, price, promise installation, or submit homeowner details without their consent.
              </Label>
            </label>

            <div className="pt-2">
              <Button onClick={handleAccept} disabled={!allChecked || submitting} size="lg" className="w-full sm:w-auto">
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Recording acceptance…
                  </>
                ) : (
                  "Accept and unlock my introducer hub"
                )}
              </Button>
              <p className="text-xs text-muted-foreground mt-3">
                Agreement version {AGREEMENT_VERSION}. Your acceptance will be recorded with a timestamp.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </PortalLayout>
  );
}
